// To support non-POST simulators, add optional `method` and `queryTemplate`
// fields to ProcessorWebhookConfig and branch in src/api/seeds.ts.
export type ProcessorWebhookConfig = {
  id: string;
  label: string;
  buildSampleBody: () => Record<string, unknown>;
  primer: {
    summary: string;
    fields: {
      path: string;
      description: string;
      example?: string;
    }[];
    notes?: string[];
  };
};

// 8 chars of base36 entropy. Appended as a trailing "_<suffix>" segment so
// the server's `rest.split('_')` parser, which destructures only the first
// three segments, ignores it while the full transactionId stays unique
// across regenerations. Without this, hitting "Reset to sample" + Send
// twice in a row trips the "already_exists" skip path.
const randomSuffix = () => Math.random().toString(36).slice(2, 10);

// A regex-valid EVM placeholder. Admins replace this with the address on
// their seeded BusinessPaymentMethod before sending; the placeholder will
// pass server-side validation and short-circuit on payment_method_not_found.
const DEFAULT_EVM_PLACEHOLDER = '0xdEadBeEfDeAdBeEfDeadbeefDEADbeef00112233';

export const PROCESSOR_WEBHOOKS: ProcessorWebhookConfig[] = [
  {
    id: 'utila',
    label: 'Utila',
    buildSampleBody: () => {
      const suffix = randomSuffix();
      return {
        id: `evt_test_${suffix}`,
        vault: 'vaults/v1',
        type: 'TRANSACTION_STATE_UPDATED',
        resourceType: 'TRANSACTION',
        resource: `vaults/v1/transactions/sim_${DEFAULT_EVM_PLACEHOLDER}_USDC_25_${suffix}`,
        details: {
          transactionStateUpdated: {
            newState: 'CONFIRMED',
          },
        },
      };
    },
    primer: {
      summary:
        'Simulates a Utila webhook delivery against UtilaWebhookProcessor. The non-prod-only server endpoint skips signature and idempotency checks, so what you POST is what the processor sees. Use it to confirm a deposit credit fires against a seeded BusinessPaymentMethod without standing up a real Utila sandbox transaction.',
      fields: [
        {
          path: 'resource',
          description:
            "Encodes the simulated transaction via the sim_<address>_<USDT|USDC>_<amount> sentinel. Address must be a valid EVM (0x + 40 hex), Tron (T + 33 base58), or Solana (32-44 base58) address; asset must be USDT or USDC (case-insensitive, no fallback); amount must be a positive number. Replace the placeholder address with the address on your seeded BusinessPaymentMethod. Any trailing _<suffix> is ignored by the server parser and is what keeps regenerated samples unique so re-sends do not hit the 'already_exists' skip.",
          example: `vaults/v1/transactions/sim_${DEFAULT_EVM_PLACEHOLDER}_USDC_25`,
        },
        {
          path: 'details.transactionStateUpdated.newState',
          description:
            "Which Utila transaction state to simulate. Use CONFIRMED to drive a deposit credit; other states exercise the processor's branching but do not credit.",
        },
        {
          path: 'id',
          description:
            'Event id used only for log attribution. Auto-randomized on Reset to sample; any non-empty string is fine if you edit it by hand.',
        },
      ],
      notes: [
        'The server endpoint is gated by disallowAccessOnProd plus admin auth, so this only works in non-prod environments.',
        'type and resourceType should stay as TRANSACTION_STATE_UPDATED / TRANSACTION for the credit-on-confirm flow.',
      ],
    },
  },
];
